/* eslint-disable react-refresh/only-export-components */
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import { friendService } from '../services/friendService'
import {useUser} from './UserContext'
import React from 'react'

interface FriendRequestCountValue {
  count: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

// Backstop poll interval (ms) — fires only while the tab is visible
const BACKSTOP_INTERVAL_MS = 120000
// Skip a focus/visibility-triggered refetch if one happened this recently (ms)
const REFETCH_THROTTLE_MS = 10000

const FriendRequestCountContext = createContext<FriendRequestCountValue | null>(null)

/**
 * Single source of truth for the pending (incoming) friend-request count used by
 * the nav badge. Shared by the nav, mega-menu and footer — one fetch and one
 * refresh mechanism instead of one poller per consumer.
 *
 * Refreshes on mount, on the `friendRequestsChanged` event (the user acting on
 * their own requests), and when the tab regains focus/visibility, with a gentle
 * visible-only backstop. No background traffic while the tab is hidden or idle.
 * Only active for users with the PLAYER role (friends feature is player-only).
 */
export const FriendRequestCountProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [count, setCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const {user} = useUser()
  const lastFetchedRef = useRef<number>(0)

  const isPlayer = user?.roles?.includes('PLAYER')

  const fetchCount = useCallback(async (): Promise<void> => {
    if (!isPlayer) {
      setCount(0)
      setLoading(false)
      return
    }

    try {
      const requests = await friendService.getPendingRequests()
      setCount(requests.length)
    } catch (err) {
      console.error('Error fetching friend request count:', err)
      setCount(0)
    } finally {
      setLoading(false)
      lastFetchedRef.current = Date.now()
    }
  }, [isPlayer])

  useEffect(() => {
    if (!isPlayer) {
      setCount(0)
      setLoading(false)
      return
    }

    fetchCount()

    // Instant refresh when the user accepts/declines/cancels on the Friends page
    const handleChange = () => fetchCount()
    window.addEventListener('friendRequestsChanged', handleChange)

    // Refetch when the tab regains focus/visibility — throttled to avoid storms
    const handleRefocus = () => {
      if (document.hidden) return
      if (Date.now() - lastFetchedRef.current < REFETCH_THROTTLE_MS) return
      fetchCount()
    }
    document.addEventListener('visibilitychange', handleRefocus)
    window.addEventListener('focus', handleRefocus)

    // Backstop: catch incoming requests during a long active session, visible-only
    const interval = setInterval(() => {
      if (!document.hidden) fetchCount()
    }, BACKSTOP_INTERVAL_MS)

    return () => {
      clearInterval(interval)
      window.removeEventListener('friendRequestsChanged', handleChange)
      document.removeEventListener('visibilitychange', handleRefocus)
      window.removeEventListener('focus', handleRefocus)
    }
  }, [isPlayer, fetchCount])

  const value: FriendRequestCountValue = useMemo(
    () => ({count, loading, refresh: fetchCount}),
    [count, loading, fetchCount]
  )

  return (
    <FriendRequestCountContext.Provider value={value}>
      {children}
    </FriendRequestCountContext.Provider>
  )
}

export const useFriendRequestCount = (): FriendRequestCountValue => {
  const context = useContext(FriendRequestCountContext)
  if (!context) {
    throw new Error('useFriendRequestCount must be used within a FriendRequestCountProvider')
  }
  return context
}
